import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import modelClassRequire from "@kaspernj/api-maker/build/model-class-require.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "role_id": {
      "column": {
        "default": null,
        "name": "role_id",
        "null": false,
        "type": null
      },
      "name": "role_id",
      "selected_by_default": null,
      "translated": null
    },
    "user_id": {
      "column": {
        "default": null,
        "name": "user_id",
        "null": false,
        "type": null
      },
      "name": "user_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "UserRole",
  "collectionKey": "user_roles",
  "collectionName": "user_roles",
  "i18nKey": "user_role",
  "camelizedLower": "userRole",
  "name": "UserRole",
  "nameDasherized": "user-role",
  "pluralName": "user_roles",
  "ransackable_associations": [
    {
      "className": "UserRole::Audit",
      "collectionName": "user_role_audits",
      "foreignKey": "user_role_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "UserRoleAudit",
      "through": null
    },
    {
      "className": "UserRole::Audit",
      "collectionName": "user_role_audits",
      "foreignKey": "user_role_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "UserRoleAudit",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "role",
      "macro": "belongs_to",
      "resource_name": "Role",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "role_id",
      "column": {
        "default": null,
        "name": "role_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "user_id",
      "column": {
        "default": null,
        "name": "user_id",
        "null": false,
        "type": null
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "role",
      "macro": "belongs_to",
      "resource_name": "Role",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "paramKey": "user_role",
  "primaryKey": "id"
}

/** Frontend model for UserRole. */
class UserRole extends BaseModel {
  /** @returns {Record<string, any>} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  roleId() {
    return this.readAttributeUnderscore("role_id")
  }

  /** @returns {boolean} */
  hasRoleId() {
    const value = this.roleId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  userId() {
    return this.readAttributeUnderscore("user_id")
  }

  /** @returns {boolean} */
  hasUserId() {
    const value = this.userId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./role.js").default | null} */
  role() {
    return this._readBelongsToReflection({reflectionName: "role"})
  }

  /** @returns {Promise<import("./role.js").default | null>} */
  loadRole() {
    if (!("roleId" in this)) throw new Error("Foreign key method wasn't defined: roleId")

    const id = this.roleId()
    const modelClass = modelClassRequire("Role")
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {reflectionName: "role", model: this, modelClass},
      {ransack}
    )
  }

  /** @returns {import("./user.js").default | null} */
  user() {
    return this._readBelongsToReflection({reflectionName: "user"})
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  loadUser() {
    if (!("userId" in this)) throw new Error("Foreign key method wasn't defined: userId")

    const id = this.userId()
    const modelClass = modelClassRequire("User")
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {reflectionName: "user", model: this, modelClass},
      {ransack}
    )
  }
}

export default UserRole
